'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/i18n/client';
import { sample, WORD_PAIRS, type WordPair } from '@/content/games';
import { playCorrect, playLose, playWin, playWrong } from '@/lib/sound';
import type { GameFinish } from './WordMatch';

const DEFAULT_ROUNDS = 5;
const MAX_WRONG = 6;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Hangman-style: guess the English word letter by letter (Kinyarwanda hint). */
export function WordGuess({
  onFinish,
  pairs = WORD_PAIRS,
}: {
  onFinish: (result: GameFinish) => void;
  pairs?: WordPair[];
}) {
  const t = useT();
  const [round, setRound] = useState(0);
  const ROUNDS = Math.min(DEFAULT_ROUNDS, pairs.length) || DEFAULT_ROUNDS;
  const words = useMemo(() => sample(pairs, ROUNDS), [round, pairs, ROUNDS]);
  const [index, setIndex] = useState(0);
  const [guessed, setGuessed] = useState<Set<string>>(new Set());
  const [done, setDone] = useState(false);
  const [solved, setSolved] = useState(0);
  const solvedRef = useRef(0);

  const pair = words[index];
  const word = (pair?.en ?? '').toUpperCase();
  const letters = useMemo(() => word.split('').filter((c) => /[A-Z]/.test(c)), [word]);
  const wrong = [...guessed].filter((g) => !word.includes(g)).length;
  const isSolved = letters.length > 0 && letters.every((l) => guessed.has(l));
  const isFailed = wrong >= MAX_WRONG;
  const over = isSolved || isFailed;

  // Fire the win/lose cue once, when a word resolves.
  const wasOver = useRef(false);

  useEffect(() => {
    setGuessed(new Set());
    wasOver.current = false;
  }, [index, round]);
  useEffect(() => {
    if (over && !wasOver.current) {
      if (isSolved) playWin();
      else playLose();
    }
    wasOver.current = over;
  }, [over, isSolved]);

  function guess(letter: string) {
    if (over || guessed.has(letter)) return;
    if (word.includes(letter)) playCorrect();
    else playWrong();
    setGuessed((prev) => new Set(prev).add(letter));
  }

  function advance() {
    if (isSolved) {
      solvedRef.current += 1;
      setSolved((n) => n + 1);
    }
    if (index + 1 < words.length) {
      setIndex(index + 1);
    } else {
      setDone(true);
      onFinish({ correct: solvedRef.current, total: ROUNDS, score: solvedRef.current * 100 });
    }
  }

  function playAgain() {
    solvedRef.current = 0;
    setSolved(0);
    setIndex(0);
    setDone(false);
    setRound((r) => r + 1);
  }

  if (done) {
    return (
      <div className="vn-game2 vn-game2--center">
        <p className="vn-game2__result-title">{t('games.roundResult', { correct: solved, total: ROUNDS })}</p>
        <button type="button" className="vn-btn vn-btn--primary" onClick={playAgain}>
          {t('games.playAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="vn-game2">
      <div className="vn-guess__meta">
        <span className="vn-game2__status">{t('games.position', { current: index + 1, total: ROUNDS })}</span>
        <span className={`vn-guess__lives${wrong >= MAX_WRONG - 2 ? ' is-low' : ''}`}>
          {t('games.livesLeft', { count: Math.max(0, MAX_WRONG - wrong) })}
        </span>
      </div>
      <p className="vn-guess__hint">
        {t('games.hint')}: <strong>{pair?.rw}</strong>
      </p>

      <div className="vn-guess__word">
        {word.split('').map((char, i) => {
          const revealed = !/[A-Z]/.test(char) || guessed.has(char) || over;
          return (
            <span key={i} className={`vn-guess__slot${revealed && /[A-Z]/.test(char) && !guessed.has(char) && over ? ' is-missed' : ''}`}>
              {revealed ? char : ''}
            </span>
          );
        })}
      </div>

      {over ? (
        <div className="vn-game2__result">
          <p className="vn-game2__result-title">
            {isSolved ? t('games.guessSolved') : `${t('games.guessFailed')} ${word}`}
          </p>
          <button type="button" className="vn-btn vn-btn--primary" onClick={advance}>
            {index + 1 < words.length ? t('games.next') : t('games.finish')}
          </button>
        </div>
      ) : (
        <div className="vn-guess__keys">
          {ALPHABET.map((letter) => {
            const used = guessed.has(letter);
            const miss = used && !word.includes(letter);
            return (
              <button
                key={letter}
                type="button"
                className={`vn-guess__key${used ? (miss ? ' is-miss' : ' is-hit') : ''}`}
                disabled={used}
                onClick={() => guess(letter)}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
